import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Sparkles, Gift, Award, Heart } from "lucide-react";
import { api, euro } from "@/lib/api";

export default function Home() {
  const [cats, setCats] = useState([]);
  const [looks, setLooks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [featured, setFeatured] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/service-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setLooks(r.data.slice(0, 6))).catch(() => {});
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/looks/featured?limit=6").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="grid lg:grid-cols-2">
          <div className="px-6 lg:px-16 py-20 lg:py-32 flex flex-col justify-center animate-fade-up">
            <span className="kima-overline text-burgundy mb-6">Nail Boutique · Sassari</span>
            <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-espresso">
              Le tue unghie non sono un dettaglio.<br /><span className="italic text-burgundy">Sono parte della tua identità.</span>
            </h1>
            <p className="mt-8 text-espresso/70 text-base max-w-md leading-relaxed">
              KIMA crea esperienze su misura, conserva i tuoi look preferiti e ti accompagna nella scelta del trattamento più adatto.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/prenota" data-testid="hero-book" className="bg-espresso text-ivory px-8 py-4 rounded-sm hover:bg-burgundy transition-colors">Prenota il tuo appuntamento</Link>
              <Link to="/kima-match" data-testid="hero-match" className="border border-espresso text-espresso px-8 py-4 rounded-sm hover:bg-espresso hover:text-ivory transition-colors">Trova il trattamento giusto</Link>
            </div>
          </div>
          <div className="hover-zoom min-h-[380px] lg:min-h-full">
            <img src="https://customer-assets-lqy194kg.emergentagent.net/job_kima-id/artifacts/1nxyirfe_46545613-3515-4e55-8f50-7bbdc6e272bf.png" alt="Interno boutique KIMA Nails" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* KIMA Match intro */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-3 gap-8">
        {[{ icon: Sparkles, t: "KIMA Match", d: "Un percorso guidato che consiglia il trattamento perfetto per te.", to: "/kima-match" },
          { icon: Award, t: "KIMA Club", d: "Colleziona punti, sblocca livelli e accedi a vantaggi esclusivi.", to: "/kima-id" },
          { icon: Gift, t: "KIMA Daily Drop", d: "Ogni giorno un beneficio riservato ai clienti KIMA.", to: "/kima-id" }].map((c, i) => (
          <Link key={i} to={c.to} className="border border-espresso/10 bg-offwhite p-10 rounded-sm group hover:border-burgundy transition-colors">
            <c.icon className="text-burgundy mb-6" strokeWidth={1.2} size={32} />
            <h3 className="font-serif text-2xl text-espresso mb-2 flex items-center gap-2">{c.t} <ArrowUpRight size={18} className="opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
            <p className="text-espresso/60 text-sm leading-relaxed">{c.d}</p>
          </Link>
        ))}
      </section>

      {/* Service families */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <span className="kima-overline text-burgundy">Le famiglie KIMA</span>
        <h2 className="font-serif text-4xl md:text-5xl text-espresso mt-3 mb-12">Trattamenti signature</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cats.map((c) => (
            <Link key={c.id} to={`/servizi?famiglia=${c.id}`} data-testid={`family-${c.id}`} className="border border-espresso/10 p-8 rounded-sm hover:bg-espresso hover:text-ivory transition-colors group bg-offwhite">
              <div className="kima-overline text-taupe group-hover:text-ivory/60 mb-3 transition-colors">0{c.order}</div>
              <h3 className="font-serif text-2xl mb-2">{c.name}</h3>
              <p className="text-sm opacity-70">{c.tagline}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div><span className="kima-overline text-burgundy">Ispirazione</span><h2 className="font-serif text-4xl md:text-5xl text-espresso mt-3">Galleria prenotabile</h2></div>
          <Link to="/galleria" className="hidden md:flex items-center gap-1 text-sm text-espresso hover:text-burgundy transition-colors">Vedi tutto <ArrowUpRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {looks.map((l) => (
            <Link key={l.id} to="/galleria" className="hover-zoom rounded-sm relative group">
              <img src={l.image} alt={l.title} className="w-full aspect-[3/4] object-cover" />
              <div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/30 transition-colors flex items-end p-4">
                <span className="text-ivory font-serif text-lg opacity-0 group-hover:opacity-100 transition-opacity">{l.title}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured KIMA Looks from community */}
      {featured.length > 0 && (
        <section className="bg-offwhite py-24" data-testid="home-featured-looks">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between mb-12">
              <div><span className="kima-overline text-burgundy">Dalla community</span><h2 className="font-serif text-4xl md:text-5xl text-espresso mt-3">KIMA Looks del momento</h2></div>
              <Link to="/looks" className="hidden md:flex items-center gap-1 text-sm text-espresso hover:text-burgundy transition-colors">Scopri tutti <ArrowUpRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {featured.map((l) => (
                <div key={l.id} data-testid={`home-look-${l.id}`} className="rounded-sm overflow-hidden border border-espresso/10 bg-ivory group">
                  <div className="hover-zoom"><img src={l.image} alt={l.title} className="w-full aspect-[3/4] object-cover" /></div>
                  <div className="p-3">
                    <div className="font-serif text-espresso truncate text-sm">{l.title}</div>
                    <div className="flex items-center gap-1 text-xs text-taupe mt-1"><Heart size={11} /> {l.likes_count || 0}</div>
                    <button onClick={() => nav(l.service_id ? `/prenota?service=${l.service_id}&look=${l.id}` : `/prenota?look=${l.id}`)} data-testid={`home-book-look-${l.id}`} className="mt-2 w-full bg-espresso text-ivory py-1.5 rounded-sm text-xs hover:bg-burgundy transition-colors">Prenota questo look</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Artists */}
      <section className="bg-espresso text-ivory py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="kima-overline text-taupe">Il team</span>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 mb-12">Le nostre nail artist</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {staff.map((a) => (
              <div key={a.id} className="hover-zoom rounded-sm">
                <img src={a.portrait} alt={a.name} className="w-full aspect-[3/4] object-cover grayscale" />
                <div className="pt-4"><div className="font-serif text-xl">{a.name}</div><div className="text-taupe text-sm">{a.role_title}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28 text-center">
        <h2 className="font-serif text-4xl md:text-6xl text-espresso mb-8">Crea la tua identità KIMA.</h2>
        <Link to="/prenota" className="inline-block bg-espresso text-ivory px-10 py-4 rounded-sm hover:bg-burgundy transition-colors">Prenota il tuo appuntamento</Link>
      </section>
    </div>
  );
}
